import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Skeleton, Tooltip } from "@sagot/ui";
import { useState } from "react";
import { usePermissions, useRouteAgentId } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { UpgradeCard } from "../shared/plan-gate";
import { useAgent } from "../shared/use-shared-queries";
import type { ChannelOut, ChannelType } from "../shared/types";
import type { ChannelPageContext } from "./channel-context";
import { ChannelKpis, ChannelKpisSkeleton } from "./channel-kpis";
import { ChannelPerformance } from "./channel-performance";
import { messengerDetail, sortChannels } from "./channels-model";
import { EmailCard } from "./email-card";
import { MessengerCard } from "./messenger-card";
import { MessengerConnectDialog } from "./messenger-dialogs";
import { ApiCard, ComingSoonCard, MobileCard, WebappCard, WebhooksCard, WidgetCard } from "./simple-cards";
import { useAgentSummary, useChannels } from "./use-channels";

const P = "deployKnowledge.channels";

function ChannelGridSkeleton() {
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="card card-body space-y-3">
          <div className="flex justify-between">
            <Skeleton className="size-10 rounded-lg" />
            <Skeleton className="h-5 w-9 rounded-pill" />
          </div>
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </section>
  );
}

function ChannelGrid({ channels, ctx, onConnect }: { channels: readonly ChannelOut[]; ctx: ChannelPageContext; onConnect: () => void }) {
  const card = (channel: ChannelOut) => {
    switch (channel.type) {
      case "widget":
        return <WidgetCard key={channel.type} channel={channel} ctx={ctx} />;
      case "webapp":
        return <WebappCard key={channel.type} channel={channel} ctx={ctx} />;
      case "mobile":
        return <MobileCard key={channel.type} ctx={ctx} />;
      case "messenger":
        return <MessengerCard key={channel.type} channel={channel} ctx={ctx} onConnect={onConnect} />;
      case "email":
        return <EmailCard key={channel.type} channel={channel} ctx={ctx} />;
      case "api":
        return <ApiCard key={channel.type} channel={channel} ctx={ctx} />;
      default:
        return null;
    }
  };
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {sortChannels(channels).map(card)}
      <WebhooksCard ctx={ctx} />
      <ComingSoonCard />
    </section>
  );
}

function HeaderActions({ ctx, connected, onConnect }: { ctx: ChannelPageContext; connected: boolean; onConnect: () => void }) {
  const { t } = useTranslation();
  const connect = connected ? (
    <Button variant="secondary" disabled>
      <Icon name="check" />
      {t(`${P}.messengerConnected`)}
    </Button>
  ) : (
    <Button disabled={!ctx.canManage} onClick={onConnect}>
      <Icon name="plus" />
      {t(`${P}.connectMessenger`)}
    </Button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button asChild variant="secondary">
        <AppLink href={consoleRoutes.analytics(ctx.wsSlug)}>
          <Icon name="bar-chart" />
          {t(`${P}.channelAnalytics`)}
        </AppLink>
      </Button>
      {!ctx.canManage && !connected && ctx.lockedReason ? (
        <Tooltip content={ctx.lockedReason}>
          <span className="inline-flex">{connect}</span>
        </Tooltip>
      ) : (
        connect
      )}
    </div>
  );
}

/** `/w/:ws/agents/:agentId/channels` (mockups/tenant/channels.html). */
export function ChannelsPage() {
  const { t } = useTranslation();
  const { summary: workspace } = useWorkspaceContext();
  const agentId = useRouteAgentId() ?? "";
  const permissions = usePermissions(agentId);
  const agent = useAgent(agentId);
  const channels = useChannels(agentId);
  const summary = useAgentSummary(agentId, "7d");
  const [planGate, setPlanGate] = useState<ChannelType | null>(null);
  const [connecting, setConnecting] = useState(false);
  const canManage = permissions.can("channels.manage", { agentId }) && !permissions.readOnly;
  const ctx: ChannelPageContext = {
    wsSlug: workspace.slug,
    wsId: workspace.id,
    agentId,
    canManage,
    lockedReason: permissions.readOnly ? t("deployKnowledge.readOnly") : t("deployKnowledge.noPermission"),
    onPlanGate: setPlanGate,
  };
  const connected = messengerDetail(channels.data?.find((c) => c.type === "messenger")).connected;
  const agentName = agent.data?.name ?? "";
  const openConnect = () => { setConnecting(true); };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{t(`${P}.title`)}</h1>
          <p className="mt-1 text-fg-muted">
            {agentName ? (
              <>
                {t(`${P}.subtitleBefore`)} <strong className="text-fg">{agentName}</strong>
                {t(`${P}.subtitleAfter`)}
              </>
            ) : (
              t(`${P}.subtitleFallback`)
            )}
          </p>
        </div>
        <HeaderActions ctx={ctx} connected={connected} onConnect={openConnect} />
      </div>
      {planGate ? <UpgradeCard feature={t(`${P}.names.${planGate}`)} /> : null}
      {channels.isError ? (
        <div className="card">
          <ErrorState title={t(`${P}.loadError`)} description={t("deployKnowledge.loadErrorBody")} onRetry={() => void channels.refetch()} retryLabel={t("common.retry")} />
        </div>
      ) : channels.isPending ? (
        <>
          <ChannelKpisSkeleton />
          <ChannelGridSkeleton />
        </>
      ) : channels.data.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="plug"
            title={t(`${P}.emptyTitle`)}
            description={t(`${P}.emptyBody`)}
            action={
              <Button asChild>
                <AppLink href={consoleRoutes.widgetStudio(workspace.slug, agentId)}>{t(`${P}.emptyCta`)}</AppLink>
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <ChannelKpis channels={channels.data} summary={summary.data} />
          <ChannelGrid channels={channels.data} ctx={ctx} onConnect={openConnect} />
          <ChannelPerformance channels={channels.data} agentId={agentId} agentName={agentName} wsSlug={workspace.slug} />
        </>
      )}
      {connecting ? <MessengerConnectDialog ctx={ctx} open onOpenChange={setConnecting} /> : null}
    </div>
  );
}
